import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import OTPInput from "@/components/OTPInput";

/**
 * Enrollment obrigatório de TOTP.
 * Recebe um `preauth` token (scope totp_setup) e, ao confirmar o código do app,
 * chama onDone(session) com a sessão completa já aplicada.
 */
export default function TotpEnroll({ preauth, onDone }) {
  const t = useT();
  const { totpSetup, totpConfirm } = useAuth();
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await totpSetup(preauth);
        if (!alive) return;
        setQr(data.qr); setSecret(data.secret);
      } catch {
        if (alive) setErr(t("auth.totp.err.invalid"));
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preauth]);

  const confirm = async (value) => {
    const c = value ?? code;
    setErr(""); setLoading(true);
    try {
      const session = await totpConfirm(preauth, c);
      onDone(session);
    } catch (e) {
      setErr(e?.response?.data?.detail || t("auth.totp.err.invalid"));
      setCode("");
    } finally { setLoading(false); }
  };

  return (
    <div className="w-full max-w-sm space-y-5" data-testid="totp-enroll">
      <div>
        <div className="overline mb-2">{t("auth.totp.step")}</div>
        <h1 className="font-display font-black text-3xl tracking-tighter">{t("auth.totp.title")}</h1>
        <p className="text-sm text-white/50 mt-2">{t("auth.totp.subtitle")}</p>
      </div>

      <div className="flex justify-center">
        {qr ? (
          <img src={qr} alt="TOTP QR" width={192} height={192}
            className="w-48 h-48 bg-white p-2 rounded" data-testid="totp-qr" />
        ) : (
          <div className="w-48 h-48 flex items-center justify-center border border-white/10">
            <Loader2 className="w-5 h-5 animate-spin text-white/40" />
          </div>
        )}
      </div>

      {secret && (
        <div className="text-center">
          <div className="overline mb-1">{t("auth.totp.manual")}</div>
          <code className="text-xs font-mono text-white/70 break-all select-all">{secret}</code>
        </div>
      )}

      <div>
        <label className="overline block mb-2 text-center">{t("auth.totp.enterCode")}</label>
        <OTPInput value={code} onChange={setCode} onComplete={confirm} disabled={loading} error={!!err} />
      </div>

      {err && <div className="text-sm text-destructive font-mono text-center" data-testid="totp-error">{err}</div>}

      <button disabled={loading || code.length < 6} onClick={() => confirm()}
        className="btn-primary w-full" data-testid="totp-confirm-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.totp.confirm")}
      </button>
    </div>
  );
}
